import { useIntl } from 'react-intl';

import { ICombo, IItem } from '@rbi-ctg/menu';
import { useFormatCalories } from 'hooks/use-format-calories';
import { useFormatJoules } from 'hooks/use-format-joules';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useNewMainMenuContext } from 'state/main-menu';
import { getFormattedCalorieDefault, getFormattedCalorieRange, isItem, isPicker } from 'utils/menu';
import { formatCalorieRange, formatCaloriesToJoulesFromString } from 'utils/menu/calories';

interface IUseCalories {
  (): (comboOrItem: ICombo | IItem) => string;
}

export const useCalories: IUseCalories = () => {
  const { formatMessage } = useIntl();
  const { getCalories } = useNewMainMenuContext();
  const formatCalories = useFormatCalories();
  const formatJoules = useFormatJoules();

  const displayNutritionWithModifiersFromSanity = useFlag(
    LaunchDarklyFlag.DISPLAY_NUTRITION_WITH_MODIFIERS_FROM_SANITY
  );
  const enabledFormattedCaloriesForDefault = useFlag(
    LaunchDarklyFlag.ENABLE_FORMATTED_CALORIES_FOR_DEFAULT
  );
  const enableNutritionDecimals = useFlag(LaunchDarklyFlag.ENABLE_NUTRITION_DECIMALS);
  const enableEUNutritionDisplay = useFlag(LaunchDarklyFlag.ENABLE_EU_NUTRITION_DISPLAY);
  const enableMenuServiceData = useFlag(LaunchDarklyFlag.ENABLE_MENU_SERVICE_DATA);

  return comboOrItem => {
    if (enableMenuServiceData) {
      let formattedCalories: string | null | undefined = '';
      const calories = getCalories(comboOrItem);

      if (!calories) {
        return '';
      }

      if (enabledFormattedCaloriesForDefault && (isItem(comboOrItem) || isPicker(comboOrItem))) {
        formattedCalories = formatCalories(calories.default);
      } else {
        formattedCalories = formatCalorieRange({
          min: Number(calories.min),
          max: Number(calories.max),
          formatFn: formatCalories,
        });
      }

      if (displayNutritionWithModifiersFromSanity && isItem(comboOrItem)) {
        formattedCalories = formatCalories(calories.default);
      }

      if (enableEUNutritionDisplay) {
        const caloriesInJoules = formatCaloriesToJoulesFromString(formattedCalories, formatJoules);
        formattedCalories = formatMessage(
          { id: 'euNutritionValues' },
          { calories: formattedCalories, joules: caloriesInJoules }
        ) as string;
      }

      return String(formattedCalories);
    }

    if (displayNutritionWithModifiersFromSanity && isItem(comboOrItem)) {
      const nutritionWithModifiers =
        comboOrItem?.nutritionWithModifiers || comboOrItem?.nutrition || {};
      return formatCalories(nutritionWithModifiers.calories || 0);
    }

    if (enableEUNutritionDisplay) {
      const calories = enableNutritionDecimals
        ? getFormattedCalorieDefault(comboOrItem, formatCalories).replace('/', ' - ')
        : getFormattedCalorieRange(comboOrItem, formatCalories);

      const caloriesInJoules = formatCaloriesToJoulesFromString(calories, formatJoules);

      const euFormattedNutritionText = formatMessage(
        { id: 'euNutritionValues' },
        { calories, joules: caloriesInJoules }
      );

      return enableEUNutritionDisplay ? `${euFormattedNutritionText}` : calories;
    }

    return enabledFormattedCaloriesForDefault
      ? getFormattedCalorieDefault(comboOrItem, formatCalories)
      : getFormattedCalorieRange(comboOrItem, formatCalories);
  };
};
